/**
 * UploadFAB - Floating action button for photo upload
 *
 * Shows upload modal with drag-and-drop, file picker, caption input,
 * and progress indicator.
 */
import { useState, useCallback, useRef, useEffect, type DragEvent, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@wovendays/i18n';
import exifr from 'exifr';
import { apiClient } from '@/lib/api-client';
import { ChildPicker } from '@/components/ChildPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Plus, X, ImagePlus, Check, Copy } from 'lucide-react';

interface ExifData {
  takenAt: number | null;
  latitude: number | null;
  longitude: number | null;
}

async function extractExifData(file: File): Promise<ExifData> {
  try {
    const exif = await exifr.parse(file, ['DateTimeOriginal', 'latitude', 'longitude']);
    return {
      takenAt: exif?.DateTimeOriginal instanceof Date ? exif.DateTimeOriginal.getTime() : null,
      latitude: typeof exif?.latitude === 'number' ? exif.latitude : null,
      longitude: typeof exif?.longitude === 'number' ? exif.longitude : null,
    };
  } catch {
    return { takenAt: null, latitude: null, longitude: null };
  }
}

/** Compute a 64-bit perceptual dHash (difference hash) for duplicate detection. */
async function perceptualHash(file: File): Promise<string | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = new OffscreenCanvas(9, 8);
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bitmap, 0, 0, 9, 8);
    bitmap.close();
    const { data } = ctx.getImageData(0, 0, 9, 8);

    // Compute luminance for each pixel (9x8 = 72 pixels, 4 channels each)
    const gray: number[] = [];
    for (let i = 0; i < data.length; i += 4) {
      gray.push(0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!);
    }

    // dHash: compare each pixel to its right neighbor
    let hash = 0n;
    for (let y = 0; y < 8; y++) {
      for (let x = 0; x < 8; x++) {
        if (gray[y * 9 + x]! > gray[y * 9 + x + 1]!) {
          hash |= 1n << BigInt(y * 8 + x);
        }
      }
    }
    return hash.toString(16).padStart(16, '0');
  } catch {
    return null; // HEIC or unsupported format — skip dedup
  }
}

function hammingDistance(a: string, b: string): number {
  const x = BigInt('0x' + a) ^ BigInt('0x' + b);
  let count = 0;
  let bits = x;
  while (bits > 0n) { bits &= bits - 1n; count++; }
  return count;
}

const DUPLICATE_THRESHOLD = 5;

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const ALLOWED_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];
const MAX_IMAGE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_VIDEO_SIZE = 10 * 1024 * 1024 * 1024; // 10GB

interface UploadFABProps {
  familyId: string;
}

export function UploadFAB({ familyId }: UploadFABProps) {
  const { t } = useTranslation('upload');
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 lg:hidden flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-elevated hover:bg-primary-700 hover:shadow-elevated-hover transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus-visible:shadow-terracotta-glow"
        aria-label={t('fab')}
      >
        <Plus className="h-6 w-6" />
      </button>
      {showModal && createPortal(
        <UploadModal familyId={familyId} onClose={() => setShowModal(false)} />,
        document.body,
      )}
    </>
  );
}

/** Desktop upload button — wide, placed above activity sidebar */
export function UploadButton({ familyId }: UploadFABProps) {
  const { t } = useTranslation('upload');
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-600 px-4 py-3 text-sm font-medium text-white shadow-elevated hover:bg-primary-700 hover:shadow-elevated-hover transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        aria-label={t('fab')}
      >
        <ImagePlus className="h-4 w-4" />
        {t('title')}
      </button>
      {showModal && createPortal(
        <UploadModal familyId={familyId} onClose={() => setShowModal(false)} />,
        document.body,
      )}
    </>
  );
}

interface UploadFile {
  id: string;
  file: File;
  preview: string;
  mediaType: 'photo' | 'video';
  status: 'pending' | 'uploading' | 'done' | 'failed';
  progress: number;
  error?: string;
  fileHash: string | null;
  isDuplicate: boolean;
  included: boolean; // whether to include in upload (unchecked for duplicates)
}

let uploadFileCounter = 0;

function UploadModal({
  familyId,
  onClose,
}: {
  familyId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('upload');
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [caption, setCaption] = useState('');
  const [selectedChildren, setSelectedChildren] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isHashing, setIsHashing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [existingHashes, setExistingHashes] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Fetch existing hashes on mount
  useEffect(() => {
    apiClient.get<{ hashes: Record<string, string> }>(`/api/families/${familyId}/media-hashes`)
      .then(data => setExistingHashes(data.hashes))
      .catch(() => {}); // Non-critical — dedup just won't work
  }, [familyId]);

  const includedFiles = files.filter(f => f.included);
  const isSingleFile = includedFiles.length === 1;
  const doneCount = files.filter(f => f.status === 'done').length;
  const failedCount = files.filter(f => f.status === 'failed').length;
  const allDone = includedFiles.length > 0 && includedFiles.every(f => f.status === 'done' || f.status === 'failed');
  const duplicateCount = files.filter(f => f.isDuplicate).length;

  // Auto-close when all uploads succeed (only if at least one was uploaded)
  useEffect(() => {
    if (!allDone || failedCount > 0 || doneCount === 0) return;
    const timer = setTimeout(() => onClose(), 500);
    return () => clearTimeout(timer);
  }, [allDone, failedCount, doneCount, onClose]);

  const validateFile = useCallback((f: File): string | null => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      return t('validation.unsupportedFormat');
    }
    const isVideo = ALLOWED_VIDEO_TYPES.includes(f.type);
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (f.size > maxSize) {
      return t('validation.tooLarge');
    }
    return null;
  }, []);

  const addFiles = useCallback(
    async (newFiles: File[]) => {
      setError(null);

      const validFiles: UploadFile[] = [];
      for (const f of newFiles) {
        const validationError = validateFile(f);
        if (validationError) {
          setError(validationError);
          continue;
        }
        const isVideo = ALLOWED_VIDEO_TYPES.includes(f.type);
        const preview = URL.createObjectURL(f);
        validFiles.push({
          id: `file-${++uploadFileCounter}`,
          file: f,
          preview,
          mediaType: isVideo ? 'video' : 'photo',
          status: 'pending',
          progress: 0,
          fileHash: null,
          isDuplicate: false,
          included: true,
        });
      }

      if (validFiles.length === 0) return;

      // Add files immediately so previews render
      setFiles(prev => [...prev, ...validFiles]);

      // Hash files and check for duplicates
      setIsHashing(true);
      const hashes = await Promise.all(validFiles.map(f => f.mediaType === 'video' ? Promise.resolve(null) : perceptualHash(f.file)));

      setFiles(prev => {
        const existingHashValues = Object.values(existingHashes);
        // Collect hashes from files already in the list (before this batch)
        const priorHashes: string[] = prev
          .filter(f => !validFiles.some(vf => vf.id === f.id) && f.fileHash)
          .map(f => f.fileHash!);

        const batchHashes: string[] = [];

        return prev.map(f => {
          const idx = validFiles.findIndex(vf => vf.id === f.id);
          if (idx === -1) return f;

          const hash = hashes[idx];
          if (!hash) return { ...f, fileHash: null };

          // Check against existing family hashes
          let isDuplicate = existingHashValues.some(
            existing => hammingDistance(hash, existing) <= DUPLICATE_THRESHOLD
          );

          // Check against prior files in the list
          if (!isDuplicate) {
            isDuplicate = priorHashes.some(
              prior => hammingDistance(hash, prior) <= DUPLICATE_THRESHOLD
            );
          }

          // Check within this batch (first one wins)
          if (!isDuplicate) {
            isDuplicate = batchHashes.some(
              bh => hammingDistance(hash, bh) <= DUPLICATE_THRESHOLD
            );
          }

          batchHashes.push(hash);
          return { ...f, fileHash: hash, isDuplicate, included: !isDuplicate };
        });
      });
      setIsHashing(false);
    },
    [validateFile, existingHashes]
  );

  const removeFile = useCallback((id: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== id);
    });
  }, []);

  const toggleInclude = useCallback((id: string) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, included: !f.included } : f));
  }, []);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach(f => URL.revokeObjectURL(f.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadSingleFile = useCallback(async (uploadFile: UploadFile) => {
    setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, status: 'uploading' as const, progress: 10 } : f));

    try {
      if (uploadFile.mediaType === 'video') {
        // Video upload via TUS
        const { takenAt, latitude, longitude } = await extractExifData(uploadFile.file);

        setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, progress: 5 } : f));

        // Step 1: Init video upload
        const initRes = await apiClient.post<{ mediaId: string; tusEndpoint: string; streamUid: string }>('/api/media/video/init', {
          familyId,
          fileSizeBytes: uploadFile.file.size,
          fileName: uploadFile.file.name,
        });

        setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, progress: 10 } : f));

        // Step 2: TUS upload
        const { Upload } = await import('tus-js-client');
        await new Promise<void>((resolve, reject) => {
          const upload = new Upload(uploadFile.file, {
            endpoint: initRes.tusEndpoint,
            chunkSize: 50 * 1024 * 1024, // 50MB chunks
            retryDelays: [0, 1000, 3000, 5000],
            metadata: {
              filename: uploadFile.file.name,
              filetype: uploadFile.file.type,
            },
            onProgress: (bytesUploaded, bytesTotal) => {
              const p = 10 + Math.round((bytesUploaded / bytesTotal) * 75);
              setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, progress: p } : f));
            },
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          });
          upload.start();
        });

        setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, progress: 88 } : f));

        // Step 3: Confirm
        const confirmRes = await apiClient.post<{ media: { id: string } }>('/api/media/video/confirm', {
          mediaId: initRes.mediaId,
          familyId,
          streamUid: initRes.streamUid,
          caption: isSingleFile && caption.trim() ? caption.trim() : undefined,
          takenAt,
          latitude,
          longitude,
        });

        // Tag children if any
        if (selectedChildren.length > 0 && confirmRes.media?.id) {
          setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, progress: 95 } : f));
          await apiClient.put(`/api/media/${confirmRes.media.id}/children`, {
            childIds: selectedChildren,
          });
        }

        setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, status: 'done' as const, progress: 100 } : f));
        return; // Skip photo upload path
      }

      // Extract EXIF before upload (server doesn't extract EXIF)
      const { takenAt, latitude, longitude } = await extractExifData(uploadFile.file);

      // Single multipart upload to /api/media/upload (server handles WebP conversion)
      setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, progress: 15 } : f));
      const formData = new FormData();
      formData.append('file', uploadFile.file);
      formData.append('familyId', familyId);
      if (isSingleFile && caption.trim()) formData.append('caption', caption.trim());
      if (uploadFile.fileHash) formData.append('fileHash', uploadFile.fileHash);
      if (takenAt != null) formData.append('takenAt', String(takenAt));
      if (latitude != null) formData.append('latitude', String(latitude));
      if (longitude != null) formData.append('longitude', String(longitude));

      const mediaResult = await new Promise<{ media: { id: string } }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const apiBase = import.meta.env.VITE_API_URL || '';
        xhr.open('POST', `${apiBase}/api/media/upload`);

        // Copy auth cookie/headers — apiClient uses credentials: 'include'
        xhr.withCredentials = true;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const p = 20 + Math.round((e.loaded / e.total) * 65);
            setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, progress: p } : f));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              reject(new Error('Invalid response'));
            }
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));

        xhr.send(formData);
      });

      // Tag children if any
      if (selectedChildren.length > 0 && mediaResult.media?.id) {
        setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, progress: 92 } : f));
        await apiClient.put(`/api/media/${mediaResult.media.id}/children`, {
          childIds: selectedChildren,
        });
      }

      setFiles(prev => prev.map(f => f.id === uploadFile.id ? { ...f, status: 'done' as const, progress: 100 } : f));
    } catch (err) {
      setFiles(prev => prev.map(f => f.id === uploadFile.id ? {
        ...f,
        status: 'failed' as const,
        progress: 0,
        error: err instanceof Error ? err.message : t('button.uploading'),
      } : f));
    }
  }, [familyId, caption, selectedChildren, isSingleFile]);

  const handleUpload = useCallback(async () => {
    setIsUploading(true);
    const pendingFiles = files.filter(f => f.included && (f.status === 'pending' || f.status === 'failed'));

    setFiles(prev => prev.map(f => f.status === 'failed' && f.included ? { ...f, status: 'pending' as const, progress: 0, error: undefined } : f));

    for (const f of pendingFiles) {
      await uploadSingleFile(f);
    }

    queryClient.invalidateQueries({ queryKey: ['timeline', familyId] });
    queryClient.invalidateQueries({ queryKey: ['children', familyId] });
    setIsUploading(false);
  }, [files, uploadSingleFile, queryClient, familyId]);

  const handleRetryFailed = useCallback(async () => {
    setIsUploading(true);
    const failedFiles = files.filter(f => f.status === 'failed');

    for (const f of failedFiles) {
      setFiles(prev => prev.map(uf => uf.id === f.id ? { ...uf, status: 'pending' as const, progress: 0, error: undefined } : uf));
      await uploadSingleFile(f);
    }

    queryClient.invalidateQueries({ queryKey: ['timeline', familyId] });
    setIsUploading(false);
  }, [files, uploadSingleFile, queryClient, familyId]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) addFiles(droppedFiles);
    },
    [addFiles]
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (files.length === 0) return;
    handleUpload();
  };

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isUploading) onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isUploading]);

  const uploadCount = includedFiles.length;
  const dialogLabel = files.length > 1 ? t('titlePlural', { count: uploadCount }) : t('title');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={dialogLabel}>
      <div className="w-full max-w-md rounded-2xl bg-background shadow-elevated max-h-[90vh] flex flex-col border">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-serif font-bold text-foreground">
            {files.length > 1 ? t('titlePlural', { count: uploadCount }) : t('title')}
          </h3>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label={t('close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto flex-1">
          {error && (
            <Alert variant="destructive" className="mb-3">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {files.length === 0 ? (
            <div
              role="button"
              tabIndex={0}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring ${
                isDragging
                  ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                  : 'border-border/60 hover:border-muted-foreground'
              }`}
            >
              <ImagePlus className="mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">
                {t('dropzone.instructions')}
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                {t('dropzone.formats')}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/heic,image/webp,video/mp4,video/quicktime,video/webm"
                multiple
                onChange={(e) => {
                  const selected = e.target.files;
                  if (selected && selected.length > 0) addFiles(Array.from(selected));
                  e.target.value = '';
                }}
                className="hidden"
              />
            </div>
          ) : (
            <div className="space-y-3">
              {isHashing && (
                <p className="text-xs text-muted-foreground text-center" role="status">{t('duplicates.checking')}</p>
              )}

              {!isHashing && duplicateCount > 0 && !allDone && (
                <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-900/20">
                  <Copy className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 dark:text-amber-200">
                    {duplicateCount === files.length
                      ? t('duplicates.allDuplicate')
                      : t('duplicates.someDuplicate', { count: duplicateCount, total: files.length })}
                  </AlertDescription>
                </Alert>
              )}

              {/* File grid */}
              <div className="grid grid-cols-3 gap-2">
                {files.map((f) => (
                  <div key={f.id} className={`relative aspect-square overflow-hidden rounded-xl bg-muted ${!f.included && f.isDuplicate ? 'opacity-50' : ''}`}>
                    {f.mediaType === 'video' ? (
                      <div className="relative h-full w-full">
                        <video
                          src={f.preview}
                          className="h-full w-full object-cover"
                          muted
                          playsInline
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60">
                            <svg className="h-4 w-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <img src={f.preview} alt="Preview" className="h-full w-full object-cover" />
                    )}
                    {f.status === 'uploading' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      </div>
                    )}
                    {f.status === 'done' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Check className="h-6 w-6 text-green-400" strokeWidth={3} />
                      </div>
                    )}
                    {f.status === 'failed' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-red-900/40">
                        <X className="h-6 w-6 text-red-400" strokeWidth={3} />
                      </div>
                    )}
                    {f.isDuplicate && f.status === 'pending' && !isUploading && (
                      <div className="absolute inset-x-0 bottom-0 bg-amber-600/80 px-1 py-0.5 text-center">
                        <span className="text-[10px] font-medium text-white" data-testid="duplicate-badge">{t('duplicates.badge')}</span>
                      </div>
                    )}
                    {(f.status === 'pending' || f.status === 'failed') && !isUploading && (
                      <button
                        type="button"
                        onClick={() => removeFile(f.id)}
                        className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                        aria-label={t('removePhoto')}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                    {f.isDuplicate && f.status === 'pending' && !isUploading && (
                      <button
                        type="button"
                        onClick={() => toggleInclude(f.id)}
                        className={`absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                          f.included ? 'border-primary-500 bg-primary-500' : 'border-white bg-black/30'
                        }`}
                        aria-label={f.included ? t('duplicates.exclude') : t('duplicates.include')}
                      >
                        {f.included && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </button>
                    )}
                  </div>
                ))}
                {!isUploading && !allDone && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex aspect-square items-center justify-center rounded-xl border-2 border-dashed border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground transition-colors"
                    aria-label={t('addMore')}
                  >
                    <Plus className="h-6 w-6" />
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/heic,image/webp,video/mp4,video/quicktime,video/webm"
                multiple
                onChange={(e) => {
                  const selected = e.target.files;
                  if (selected && selected.length > 0) addFiles(Array.from(selected));
                  e.target.value = '';
                }}
                className="hidden"
              />

              {isSingleFile && !allDone && (
                <div>
                  <label htmlFor="caption" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    {t('captionLabel')}
                  </label>
                  <Input
                    id="caption"
                    type="text"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    disabled={isUploading}
                    placeholder={t('captionPlaceholder')}
                    maxLength={1000}
                  />
                </div>
              )}

              {!allDone && (
                <div>
                  <p className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1">{t('tagChildren')}</p>
                  <ChildPicker
                    familyId={familyId}
                    selectedIds={selectedChildren}
                    onToggle={(childId) => {
                      setSelectedChildren((prev) =>
                        prev.includes(childId)
                          ? prev.filter((id) => id !== childId)
                          : [...prev, childId]
                      );
                    }}
                  />
                </div>
              )}

              {isUploading && (
                <div className="space-y-1" role="status" aria-live="polite">
                  <Progress
                    value={includedFiles.length > 0 ? Math.round((doneCount / includedFiles.length) * 100) : 0}
                    aria-label={t('progress.label')}
                    className="h-2"
                  />
                  <p className="font-mono text-xs text-muted-foreground text-center">
                    {t('progress.uploading', { done: doneCount + 1, total: includedFiles.length })}
                  </p>
                </div>
              )}

              {allDone && (
                <p className="text-sm text-center text-foreground">
                  {failedCount === 0
                    ? t('result.success', { count: doneCount })
                    : t('result.partial', { done: doneCount, total: includedFiles.length, failed: failedCount })}
                </p>
              )}
            </div>
          )}

          {files.length > 0 && (
            <div className="mt-4 flex gap-2">
              {!allDone ? (
                <>
                  <Button
                    type="submit"
                    disabled={isUploading || isHashing || includedFiles.length === 0 || includedFiles.every(f => f.status === 'done')}
                    className="flex-1"
                  >
                    {isHashing ? t('button.checking') : isUploading ? t('button.uploading') : includedFiles.length > 1 ? t('button.uploadCount', { count: includedFiles.length }) : t('button.upload')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    disabled={isUploading}
                  >
                    {t('common:cancel')}
                  </Button>
                </>
              ) : (
                <>
                  {failedCount > 0 && (
                    <Button
                      type="button"
                      onClick={handleRetryFailed}
                      className="flex-1"
                    >
                      {t('button.retryFailed', { count: failedCount })}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                    className="flex-1"
                  >
                    {failedCount > 0 ? t('common:close') : t('common:done')}
                  </Button>
                </>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
